import { useEffect, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Combobox — single-select with free-typing fallback.
 *
 * Behaviour:
 *   • Click → opens a dropdown with the suggestion list
 *   • Type → narrows the list AND becomes the value (free-text allowed
 *     for the long tail of brands/models not in our seed)
 *   • Click outside → closes (keeps whatever was typed)
 *   • Keyboard: ↑ / ↓ to navigate, Enter to commit, Esc to close
 *
 * Props are deliberately minimal so this component can be dropped into
 * any field that has a suggestion list:
 *   - `value` (string), `onChange(string)`
 *   - `options` (string[]) — suggestions; order is respected
 *   - `placeholder`, `inputClassName`, `testid`
 *   - `allowFreeText` (default true)
 */
export default function Combobox({
  value, onChange,
  options = [],
  placeholder = "",
  inputClassName = "",
  allowFreeText = true,
  emptyLabel = "Nada encontrado",
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);

  const query = (value || "").toLowerCase().trim();
  const filtered = !query
    ? options
    : options.filter((o) => o.toLowerCase().includes(query));

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset active index when filter changes
  useEffect(() => { setActiveIdx(-1); }, [query, open]);

  const commit = (v) => {
    onChange(v);
    setOpen(false);
  };

  const handleKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && filtered[activeIdx]) {
        e.preventDefault();
        commit(filtered[activeIdx]);
      } else if (allowFreeText) {
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid ? `${testid}-wrap` : undefined}>
      <div className="relative">
        <input
          value={value || ""}
          onChange={(e) => {
            onChange(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          data-testid={testid}
          className={`${inputClassName} pr-7`}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          tabIndex={-1}
          data-testid={testid ? `${testid}-toggle` : undefined}
        >
          <CaretDown size={11} weight="bold" />
        </button>
      </div>

      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-[0_12px_28px_-12px_rgba(15,23,42,0.18)] max-h-56 overflow-y-auto"
          data-testid={testid ? `${testid}-list` : undefined}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2.5 text-[12px] text-slate-400 italic flex items-center gap-2">
              <MagnifyingGlass size={11} />
              {allowFreeText
                ? `Sem sugestão — pode digitar “${value}” mesmo assim`
                : emptyLabel}
            </div>
          ) : (
            filtered.map((opt, i) => (
              <button
                key={opt}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); commit(opt); }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-slate-50 ${
                  activeIdx === i ? "bg-slate-50" : ""
                } ${value === opt ? "font-semibold text-slate-900" : "text-slate-700"}`}
                data-testid={testid ? `${testid}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}
              >
                {opt}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
